import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { ArrowRight, BarChart3, Check, Code2, KeyRound, Server, ShieldCheck, Sparkles, Terminal, Zap } from 'lucide-react'
import { navigate } from '../lib/router'
import { useAuthStore } from '../lib/auth-store'
import { api, type ApiError } from '../lib/api'
import { cn } from '../lib/cn'
import { PublicHeader } from '../components/marketing/PublicHeader'
import { LpSectionHeader } from '../components/marketing/LpSectionHeader'
import { Reveal, ScrollRail } from '../components/marketing/Reveal'
import { Footer } from '../components/Footer'
import { Button, ButtonLink, buttonVariants } from '../components/ui/button'
import { Badge } from '../components/ui/badge'

// ─────────────────────────────────────────────────────────────────────────────
// Landing page — B2B, invoice-based, no direct payment.
// noindex is set in index.html (beta phase). Structure follows the doWallet
// marketing DNA: floating glass nav, two-column hero with one accented word,
// proof strip, alternating light / dark / volt bands, section headers with
// eyebrow + two-line headline, scroll reveals.
// ─────────────────────────────────────────────────────────────────────────────

const NAV = [
  { href: '/#features', label: 'Features' },
  { href: '/#how', label: 'How it works' },
  { href: '/#security', label: 'Security' },
  { href: '/#pricing', label: 'Pricing' },
]

export function LandingPage() {
  const accessToken = useAuthStore((s) => s.accessToken)

  return (
    <div className="min-h-svh bg-background text-foreground">
      <ScrollRail />
      <PublicHeader loggedIn={!!accessToken} nav={NAV} />
      <Hero />
      <ProofStrip />
      <HowItWorks />
      <div className="lp-seam-glow" aria-hidden />
      <Features />
      <Security />
      <Demo />
      <Pricing />
      <CTA />
      <Footer variant="site" />
    </div>
  )
}

const rise = (ms: number) => ({ '--lp-rise-delay': `${ms}ms` } as CSSProperties)

// ── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  const setSession = useAuthStore((s) => s.setSession)
  const [demoLoading, setDemoLoading] = useState(false)
  const [demoErr, setDemoErr] = useState<string | null>(null)

  async function startDemo() {
    setDemoLoading(true)
    setDemoErr(null)
    try {
      const auth = await api.startDemoSession()
      setSession(auth)
      navigate('/dashboard')
    } catch (err) {
      const ae = err as ApiError
      setDemoErr(
        ae.status === 404
          ? 'Demo is being set up — try again shortly, or request early access.'
          : ae.title ?? 'Could not start demo session',
      )
      setDemoLoading(false)
    }
  }

  return (
    <section className="lp-glow-volt relative flex min-h-svh items-center overflow-hidden pt-28 pb-16 sm:pt-32">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-14 px-6 lg:grid-cols-[minmax(0,1fr)_auto] xl:max-w-[76rem]">
        <div className="max-w-2xl">
          <span className="lp-eyebrow lp-hero-rise" data-tone="volt" style={rise(0)}>
            <span className="size-1.5 animate-pulse rounded-full bg-volt" />
            Now in private beta
          </span>

          <h1 className="lp-hero-title lp-hero-rise-solid mt-6" style={rise(80)}>
            <span className="block">Build apps with</span>
            <span className="lp-hero-title-2 block">
              natural <span className="lp-accent-word">language</span>.
            </span>
          </h1>

          <p className="lp-body-lg lp-hero-rise mt-6 max-w-xl text-muted-foreground" style={rise(160)}>
            Describe what your team needs. Claude writes the code.
            You deploy a full-stack Next.js app to a real URL — in under two minutes.
          </p>

          <div className="lp-hero-rise mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center" style={rise(240)}>
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              className="group h-[3.25rem] rounded-full px-8 text-base font-medium shadow-[0_14px_34px_-16px_rgba(0,0,0,0.45)]"
            >
              Request early access
              <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={startDemo}
              disabled={demoLoading}
              className="group h-[3.25rem] rounded-full px-6 text-base font-medium"
            >
              {demoLoading ? 'Starting demo…' : 'Try the live demo'}
              <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
          {demoErr && (
            <p className="mt-4 text-sm text-warning-ink">{demoErr}</p>
          )}

          <p className="lp-hero-rise mt-6 text-sm text-muted-foreground" style={rise(300)}>
            Your API key. Your code. Your infrastructure.
          </p>
        </div>

        <div className="lp-hero-rise justify-self-center lg:justify-self-end" style={rise(360)}>
          <HeroDevice />
        </div>
      </div>
    </section>
  )
}

/** Product mockup: prompt → build log → live URL, as a light flat card. */
function HeroDevice() {
  const steps = [
    { label: 'Setting up workspace', done: true },
    { label: 'Claude is reading the project', done: true },
    { label: 'Edited app/page.tsx · 3 files', done: true },
    { label: 'Deploying to HTTPS preview', done: false },
  ]
  return (
    <div className="lp-float w-[min(100vw-3rem,26rem)] overflow-hidden rounded-xl bg-card text-sm text-card-foreground ring-1 ring-foreground/10">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-md bg-volt-soft text-volt-ink"><Sparkles className="size-3.5" /></span>
          <span className="font-medium">onboarding-portal</span>
        </div>
        <Badge variant="volt">Work mode</Badge>
      </div>
      <div className="space-y-4 px-4 py-4">
        <div className="rounded-lg bg-muted px-3 py-2.5 text-[13px] leading-relaxed text-foreground/90">
          Build a customer onboarding portal with document upload and an approval workflow.
        </div>
        <ul className="space-y-2">
          {steps.map((s) => (
            <li key={s.label} className="flex items-center gap-2.5 text-[13px]">
              {s.done ? (
                <span className="flex size-4 items-center justify-center rounded-full bg-success-soft text-success-ink"><Check className="size-3" /></span>
              ) : (
                <span className="flex size-4 items-center justify-center"><span className="size-2 animate-pulse rounded-full bg-volt" /></span>
              )}
              <span className={s.done ? 'text-muted-foreground' : 'font-medium text-foreground'}>{s.label}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/50 px-4 py-3">
        <span className="truncate font-mono text-xs text-muted-foreground">https://onboarding.dev.doable.website</span>
        <span className={buttonVariants({ size: 'sm', className: 'pointer-events-none' })}>Deploy</span>
      </div>
    </div>
  )
}

// ── Proof strip ─────────────────────────────────────────────────────────────

function ProofStrip() {
  return (
    <section className="border-y bg-muted/20">
      <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 sm:grid-cols-3 sm:gap-6">
        <Stat value="~15s" label="per iteration" />
        <Stat value="~90s" label="to live URL" />
        <Stat value="€0.03" label="per build" />
      </div>
    </section>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center sm:text-left">
      <p className="text-3xl font-semibold tracking-tight">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  )
}

// ── How it works ────────────────────────────────────────────────────────────

const STEPS = [
  {
    num: '01',
    title: 'Describe',
    desc: 'Write what you need in plain language. No wireframes, no specs, no boilerplate. Just describe the app.',
    detail: '"Build a customer onboarding portal with document upload and an approval workflow."',
  },
  {
    num: '02',
    title: 'Iterate',
    desc: 'Chat with Claude to refine. Each message edits the same codebase — additions, not rewrites. ~15 seconds per round.',
    detail: '"Add a comments section on the approval page and send an email when a document is approved."',
  },
  {
    num: '03',
    title: 'Deploy',
    desc: 'One click. Your app goes live at a real HTTPS URL. Share it with your team, your client, your stakeholders.',
    detail: 'https://onboarding.dev.doable.website',
  },
]

function HowItWorks() {
  return (
    <section id="how" className="lp-section scroll-mt-20">
      <div className="mx-auto max-w-6xl px-6">
        <LpSectionHeader eyebrow="How it works" line1="Three steps from idea" line2="to production." />
        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 80} className="lp-card flex flex-col bg-card p-7 ring-1 ring-foreground/10">
              <Badge variant="volt" className="w-fit font-mono">{step.num}</Badge>
              <h3 className="mt-5 text-xl font-medium tracking-tight">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
              <p className="mt-5 rounded-lg bg-muted px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">{step.detail}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Features (dark band) ────────────────────────────────────────────────────

const FEATURES = [
  {
    title: 'BYO API key',
    desc: 'Bring your own Anthropic API key. We never see your token spend. Anthropic bills you directly — no middleman, no markup.',
    icon: <KeyRound />,
  },
  {
    title: 'Real infrastructure',
    desc: 'Every app deploys to a hardened Docker container with HTTPS, behind nginx, on Hetzner EU servers. Not a sandbox — production-grade.',
    icon: <Server />,
  },
  {
    title: 'Code you own',
    desc: 'Full Next.js 15 + TypeScript + PostgreSQL source code. Export to GitHub. Fork it. Modify it. Zero lock-in.',
    icon: <Code2 />,
  },
  {
    title: 'Fast iteration',
    desc: 'Claude edits the same workspace across messages — no re-generation from scratch. ~15 seconds per prompt. Deploy when ready.',
    icon: <Zap />,
  },
  {
    title: 'Powered by Claude Code',
    desc: 'The same Claude Code CLI developers trust — running headless in a hardened container with full tool access, file editing, and bash.',
    icon: <Terminal />,
  },
  {
    title: 'Build history & costs',
    desc: 'Every build is logged with prompts, file changes, duration, and Anthropic cost. Full visibility, no surprises.',
    icon: <BarChart3 />,
  },
]

function Features() {
  return (
    <section id="features" className="lp-dark lp-section scroll-mt-20">
      <div className="mx-auto max-w-6xl px-6">
        <LpSectionHeader eyebrow="Features" line1="Built for teams" line2="that ship." />
        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 60} className="lp-card border border-white/10 bg-white/[0.03] p-7">
              <div className="flex size-10 items-center justify-center rounded-md bg-white/10 text-white/80 [&_svg]:size-5">
                {f.icon}
              </div>
              <h3 className="mt-5 text-base font-medium text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--lp-dark-muted)]">{f.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Security ────────────────────────────────────────────────────────────────

const SECURITY = [
  ['API keys encrypted at rest', 'AES-256-GCM with per-deployment master keys. Never stored in plaintext. Never logged.'],
  ['EU hosting (Hetzner, Germany)', 'All data processed and stored in Hetzner Cloud, Falkenstein, Germany. GDPR-compliant.'],
  ['Container isolation', 'Every deployed app runs in a read-only container with zero Linux capabilities, memory limits, and no network access to other tenants.'],
  ['WireGuard encrypted mesh', 'All inter-server traffic travels over an encrypted WireGuard VPN. No plaintext on the wire, ever.'],
  ['Outbound firewall whitelist', 'Hetzner Cloud Firewalls restrict outbound traffic to DNS, NTP, HTTPS only. No unrestricted internet access.'],
  ['Audit logging', 'Every login, build, deploy, and delete is logged with user, IP, timestamp. Exportable for compliance.'],
]

function Security() {
  return (
    <section id="security" className="lp-section scroll-mt-20 bg-muted/30">
      <div className="mx-auto max-w-6xl px-6">
        <LpSectionHeader
          eyebrow="Security"
          line1="Enterprise-grade"
          line2="from day one."
          sub="We built Doable the way we'd want a vendor to build it for us."
        />
        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {SECURITY.map(([title, desc], i) => (
            <Reveal key={title} delay={i * 50} className="flex gap-4 rounded-xl bg-card p-5 ring-1 ring-foreground/10">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-volt-soft text-volt-ink"><ShieldCheck className="size-4" /></span>
              <div>
                <h3 className="text-sm font-semibold">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Interactive Demo (simulated, dark product panel) ────────────────────────

const DEMO_STEPS = [
  { delay: 400, text: '▸ Setting up workspace' },
  { delay: 800, text: '▸ Claude is reading the project' },
  { delay: 1500, text: '  📖 Read app/page.tsx' },
  { delay: 600, text: '  Creating the landing page component.' },
  { delay: 1200, text: '  ✏️ Edited app/page.tsx' },
  { delay: 800, text: '  📝 Wrote app/globals.css' },
  { delay: 600, text: '  ✏️ Edited app/layout.tsx' },
  { delay: 400, text: '  Done!' },
  { delay: 300, text: '  ✓ Claude finished in 8.2s ($0.05)' },
  { delay: 500, text: '▸ Changes saved — ready to deploy' },
]

function Demo() {
  const [prompt, setPrompt] = useState('')
  const [running, setRunning] = useState(false)
  const [lines, setLines] = useState<string[]>([])
  const [done, setDone] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const runDemo = useCallback(async () => {
    if (running) return
    setRunning(true)
    setLines([])
    setDone(false)

    for (const step of DEMO_STEPS) {
      await new Promise((r) => setTimeout(r, step.delay))
      setLines((prev) => [...prev, step.text])
    }
    setDone(true)
    setRunning(false)
  }, [running])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [lines])

  return (
    <section id="demo" className="lp-section scroll-mt-20">
      <div className="mx-auto max-w-5xl px-6">
        <LpSectionHeader
          eyebrow="Try it"
          eyebrowTone="volt"
          line1="See how it feels."
          sub="Type a prompt below and watch a simulated build run."
          align="center"
        />

        <Reveal className="lp-panel mt-12 text-white">
          {/* Prompt bar */}
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !running && prompt.trim() && runDemo()}
              placeholder="e.g. Build me a customer feedback dashboard with charts"
              disabled={running}
              className="h-9 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/40 disabled:opacity-50"
            />
            <Button size="sm" onClick={runDemo} disabled={running || !prompt.trim()} className="font-semibold">
              {running ? 'Building…' : 'Build'}
            </Button>
          </div>

          {/* Terminal */}
          <div ref={scrollRef} className="h-64 overflow-y-auto px-5 py-5 font-mono text-[12px] leading-relaxed">
            {lines.length === 0 ? (
              <p className="text-white/35">Type a prompt and press Enter to see a simulated build…</p>
            ) : (
              lines.map((line, i) => (
                <p
                  key={i}
                  className={cn(
                    line.startsWith('▸')
                      ? 'mt-1 font-semibold text-white/80'
                      : line.includes('✓')
                        ? 'text-white/80'
                        : line.includes('⚠')
                          ? 'text-warning'
                          : 'text-[color:var(--lp-dark-muted)]',
                  )}
                >
                  {line}
                </p>
              ))
            )}
            {running && <span className="inline-block h-3 w-1.5 animate-pulse bg-volt-on-dark" />}
          </div>

          {/* Status bar */}
          <div className="flex items-center justify-between border-t border-white/10 px-5 py-3 text-xs">
            <span className={done ? 'text-white/80' : 'text-white/45'}>
              {done ? '✓ Ready to deploy' : running ? 'Claude is working…' : 'Idle'}
            </span>
            {done && (
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="lp-arrow-link inline-flex items-center gap-1 font-medium text-white/80 transition-opacity hover:opacity-80"
              >
                Try it for real <ArrowRight className="size-3.5" />
              </button>
            )}
          </div>
        </Reveal>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          This is a simulated preview. The real product connects to Claude Code CLI and deploys actual apps.
        </p>
      </div>
    </section>
  )
}

// ── Pricing (dark band) ─────────────────────────────────────────────────────

const BETA_FEATURES = ['Unlimited projects', 'Unlimited iterations', 'HTTPS preview URLs', 'Build history + cost tracking', 'BYO Anthropic API key']
const TEAMS_FEATURES = ['Everything in Beta', 'SSO (SAML / OIDC)', 'Team workspaces + RBAC', 'Custom domain support', 'Priority support', 'Self-hosted option']

function Pricing() {
  return (
    <section id="pricing" className="lp-dark lp-section scroll-mt-20">
      <div className="mx-auto max-w-6xl px-6">
        <LpSectionHeader
          eyebrow="Pricing"
          line1="Simple, transparent pricing."
          sub="No credit card. No per-seat fees during beta. You only pay Anthropic for the AI usage — we invoice quarterly for the platform."
          align="center"
        />

        <div className="mx-auto mt-14 grid max-w-4xl gap-4 md:grid-cols-2">
          <Reveal className="lp-card flex flex-col border border-white/10 bg-white/[0.03] p-7">
            <p className="text-xs font-medium uppercase tracking-wider text-white/80">Beta</p>
            <p className="mt-3 text-4xl font-medium tracking-tight text-white">Free</p>
            <p className="mt-1 text-sm text-[color:var(--lp-dark-muted)]">During private beta</p>
            <ul className="mt-6 space-y-3 text-sm text-[color:var(--lp-dark-muted)]">
              {BETA_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5"><Check className="size-4 shrink-0 text-white/80" /> {f}</li>
              ))}
            </ul>
            <Button size="lg" onClick={() => navigate('/signup')} className="mt-8 h-11 w-full bg-console-foreground text-console hover:bg-console-foreground/90">
              Request access
            </Button>
          </Reveal>

          <Reveal delay={80} className="lp-card flex flex-col border border-white/20 bg-white/[0.06] p-7">
            <p className="text-xs font-medium uppercase tracking-wider text-white/80">Teams</p>
            <p className="mt-3 text-4xl font-medium tracking-tight text-white">Custom</p>
            <p className="mt-1 text-sm text-[color:var(--lp-dark-muted)]">Quarterly invoicing</p>
            <ul className="mt-6 space-y-3 text-sm text-[color:var(--lp-dark-muted)]">
              {TEAMS_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5"><Check className="size-4 shrink-0 text-white/80" /> {f}</li>
              ))}
            </ul>
            <ButtonLink
              href="mailto:hello@doable.website?subject=Doable Teams — inquiry"
              variant="outline"
              size="lg"
              className="mt-8 h-11 w-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Contact us
            </ButtonLink>
          </Reveal>
        </div>

        <p className="mt-8 text-center text-xs text-[color:var(--lp-dark-muted)]">
          Anthropic API costs are billed directly by Anthropic to your account. Doable never marks up or resells inference.
        </p>
      </div>
    </section>
  )
}

// ── CTA (volt band) ─────────────────────────────────────────────────────────

function CTA() {
  return (
    <section className="lp-dark lp-section-sm">
      <Reveal className="mx-auto max-w-2xl px-6 text-center">
        <h2 className="lp-h3">Ready to build?</h2>
        <p className="lp-sub lp-body-lg mt-4">
          Join the private beta. Start shipping internal tools, prototypes, and MVPs in minutes instead of weeks.
        </p>
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate('/signup')}
          className="group mt-8 h-[3.25rem] rounded-full border-white/60 bg-transparent px-8 text-base font-medium text-white hover:bg-white/10 hover:text-white"
        >
          Request early access
          <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
        </Button>
      </Reveal>
    </section>
  )
}
